import React, { useEffect, useState } from "react";
import ConfirmationDialog from "./ConfirmationDialog";
import { useGlobalState } from "../contexts/GlobalStateContext";
import UpdateEmailModal from "./UpdateEmailModal";
import UpdatePasswordModal from "./UpdatePasswordModal";
import { useNavigate } from "react-router-dom";

const ProfileMenu = ({ context, onClose }) => {
  const [userSettings, setUserSettings] = useState({ TwoFA: false, LA: false });
  const { globalState, resetGlobalState, setHasPublicPosts, setUser, setAuth, logOut } =
    useGlobalState();
  const navigate = useNavigate();
  const [formResult, setFormResult] = useState(null);

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [currentAction, setCurrentAction] = useState("");

  const [isUpdateEmailModalOpen, setIsUpdateEmailModalOpen] = useState(false);
  const [isUpdatePasswordModalOpen, setIsUpdatePasswordModalOpen] =
    useState(false);

  const [statusMessage, setStatusMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loadingDots, setLoadingDots] = useState("");
  const [loadingIntervalId, setLoadingIntervalId] = useState(null);

  const checkAuth = async () => {
    try {
      const response = await fetch(`/php/check_auth.php`, {
        credentials: "include",
      });

      if (!response.ok)
        throw new Error("Failed to fetch authentication status");

      const data = await response.json();
      if (data.authenticated) {
        setUser(data);
      } else {
        setAuth(false);
        setUser(null);
      }
    } catch (error) {
      console.error("Error checking auth:", error);
      setAuth(false);
      setUser(null);
    }
  };

  useEffect(() => {
    if (context === "settings") {
      fetch("/php/get_user_settings.php")
        .then((res) => res.json())
        .then((data) => {
          if (data.success) {
            setUserSettings({
              TwoFA: data.twoFactorAuth,
              LA: data.loginAlerts,
            });
          }
        });
    }

    if (context === "privacy") {
      fetch("/php/check_if_public_posts_exist.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      })
        .then((res) => res.json())
        .then((data) => {
          setHasPublicPosts(data.hasPublicPosts);
        });
    }
  }, [context]);

  useEffect(() => {
    if (statusMessage) {
      let dotCount = 0;
      let increasing = true;
      const id = setInterval(() => {
        if (increasing) {
          dotCount++;
          if (dotCount === 3) increasing = false;
        } else {
          dotCount--;
          if (dotCount === 0) increasing = true;
        }
        setLoadingDots(".".repeat(dotCount));
      }, 500);
      setLoadingIntervalId(id);
      return () => clearInterval(id);
    }
  }, [statusMessage]);

  const stopLoading = () => {
    if (loadingIntervalId) {
      clearInterval(loadingIntervalId);
      setLoadingIntervalId(null);
    }
    setLoadingDots("");
  };

  const deactivateAccount = async (password) => {
    setStatusMessage("Deactivating");
    try {
      const res = await fetch("/php/deactivate_account.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      stopLoading();
      if (data.success) {
        setStatusMessage("Account deactivated successfully!");
        logOut();
        resetGlobalState();
        navigate("/");
      } else {
        setStatusMessage("");
        setErrorMessage(data.message || "Failed to deactivate account.");
      }
    } catch (err) {
      stopLoading();
      setStatusMessage("");
      setErrorMessage("An error occurred while deactivating your account.");
    }
  };

  const deleteAccount = async (password) => {
    setStatusMessage("Deleting");
    try {
      const res = await fetch("/php/delete_account.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      stopLoading();
      if (data.success) {
        setStatusMessage("Account deleted successfully!");
        logOut();
        resetGlobalState();
        navigate("/");
      } else {
        setStatusMessage("");
        setErrorMessage(data.message || "Failed to delete account.");
      }
    } catch (err) {
      stopLoading();
      setStatusMessage("");
      setErrorMessage("An error occurred while deleting your account.");
    }
  };

  const makePostsPrivate = async () => {
    setStatusMessage("Making Posts Private");
    try {
      const res = await fetch("/php/make_public_posts_private.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json();
      stopLoading();
      if (data.success) {
        setStatusMessage("Making all of your public posts private");
        setTimeout(() => {
          setStatusMessage("");
          setHasPublicPosts(false);
        }, 2000);
      } else if (data.status === "error"){
        logOut();
        resetGlobalState();
      } else {
        setStatusMessage("");
        setErrorMessage(data.message || "Failed to make posts private.");
      }
    } catch (err) {
      stopLoading();
      setStatusMessage("");
      setErrorMessage("An error occurred while making posts private.");
    }
  };

  const signOutEverywhereElse = async () => {
    setStatusMessage("Signing Out Everywhere Else");
    try {
      const res = await fetch("/php/sign_out_everywhere_else.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json();
      stopLoading();
      if (data.success) {
        checkAuth();
        setTimeout(() => {
          setStatusMessage("");
        }, 2000);
      } else if (data.status === "error"){
        logOut();
        resetGlobalState();
      } else {
        setStatusMessage("");
        setErrorMessage(data.message || "Failed to make posts private.");
      }
    } catch (err) {
      stopLoading();
      setStatusMessage("");
      setErrorMessage("An error occurred while signing out.");
    }
  };

  const handleToggle = (setting) => {
    setUserSettings((prevSettings) => ({
      ...prevSettings,
      [setting]: !prevSettings[setting],
    }));

    fetch("/php/toggle_setting.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ setting }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (!data.success) {
          alert(`Error toggling ${setting}: ${data.message}`);
          setUserSettings((prevSettings) => ({
            ...prevSettings,
            [setting]: !prevSettings[setting],
          }));
        }
      })
      .catch(() => {
        alert(`An error occurred while toggling ${setting}.`);
        setUserSettings((prevSettings) => ({
          ...prevSettings,
          [setting]: !prevSettings[setting],
        }));
      });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = globalState.user.fullName;
    const email = globalState.user.email;
    const formData = {
      name,
      email,
      subject: e.target.subject.value,
      message: e.target.message.value,
    };

    try {
      const res = await fetch("/php/submit_form.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const result = await res.json();
      if (result.status === "success") {
        setFormResult("Form submitted successfully");
      } else {
        alert("Error: " + result.message);
      }
    } catch (err) {
      alert("An error occurred.");
    }
  };

  const handleConfirmAction = (passwordInput) => {
    setErrorMessage("");

    switch (currentAction) {
      case "deactivate":
        if (!passwordInput) {
          setErrorMessage("Password is required to deactivate account.");
          return;
        }
        deactivateAccount(passwordInput);
        break;

      case "delete":
        if (!passwordInput) {
          setErrorMessage("Password is required to delete account.");
          return;
        }
        deleteAccount(passwordInput);
        break;

      case "makePrivate":
        makePostsPrivate();
        break;
      case "signOutEverywhereElse":
        signOutEverywhereElse();
        break;

      default:
        break;
    }

    setShowConfirmDialog(false);
  };

  const handleCancelAction = () => {
    setShowConfirmDialog(false);
  };

  const updateEmail = (email, password, setNotice) => {
    if (email && password) {
      setNotice("Email updated successfully!");
    } else {
      setNotice("Please fill in all fields.");
    }
  };

  const renderSettings = () => (
    <>
      {isUpdateEmailModalOpen && (
        <UpdateEmailModal
          onClose={() => setIsUpdateEmailModalOpen(false)}
          onUpdate={updateEmail}
        />
      )}
      {isUpdatePasswordModalOpen && (
        <UpdatePasswordModal
          onClose={() => setIsUpdatePasswordModalOpen(false)}
        />
      )}
      <h2>Settings</h2>
      {["TwoFA", "LA"].map((id) => (
        <div className="toggle-wrapper" key={id}>
          <label className="toggle-label">
            {id === "TwoFA" ? "Two Factor Authentication" : "Login Alerts"}
          </label>
          <label className="switch">
            <input
              type="checkbox"
              checked={userSettings[id]}
              onChange={() => handleToggle(id)}
            />
            <span className="slider round"></span>
          </label>
        </div>
      ))}
      <button
        className="update-button"
        onClick={() => setIsUpdateEmailModalOpen(true)}
      >
        Update Email Address
      </button>
      <button
        className="update-button"
        onClick={() => setIsUpdatePasswordModalOpen(true)}
      >
        Update Password
      </button>
      <div className="button-wrapper">
        <button
          className="delete-account-button"
          onClick={() => {
            setCurrentAction("deactivate");
            setShowConfirmDialog(true);
          }}
        >
          Deactivate Account
        </button>
        <button
          className="delete-account-button"
          onClick={() => {
            setCurrentAction("delete");
            setShowConfirmDialog(true);
          }}
        >
          Delete Account
        </button>

        <button
          className="sign-out-all-button"
          onClick={() => {
            setCurrentAction("signOutEverywhereElse");
            setShowConfirmDialog(true);
          }}
        >
          Sign Out Everywhere Else
        </button>
      </div>
    </>
  );

  const renderPrivacy = () => (
    <div className="button-wrapper">
      <button
        className="delete-account-button"
        disabled={!globalState.hasPublicPosts}
        onClick={() => {
          setCurrentAction("makePrivate");
          setShowConfirmDialog(true);
        }}
      >
        {globalState.hasPublicPosts
          ? "Make All Public Posts Private"
          : "You have no public posts"}
      </button>
    </div>
  );

  const renderSupport = () => {
    const name = globalState.user.fullName;
    const email = globalState.user.email;

    return (
      <form className="contact-form" onSubmit={handleSubmit}>
        <label htmlFor="s-name">Name:</label>
        <input type="text" id="s-name" value={name} readOnly />

        <label htmlFor="s-email">Email:</label>
        <input type="email" id="s-email" value={email} readOnly />

        <label htmlFor="subject">Subject:</label>
        <input
          type="text"
          id="subject"
          name="subject"
          placeholder="Enter subject"
          required
        />

        <label htmlFor="message">Message:</label>
        <textarea
          id="message"
          name="message"
          placeholder="Enter your message"
          required
        ></textarea>

        <button type="submit">Submit</button>
        {formResult && <p style={{ textAlign: "center" }}>{formResult}</p>}
      </form>
    );
  };

  const renderContent = () => {
    switch (context) {
      case "settings":
        return renderSettings();
      case "privacy":
        return renderPrivacy();
      case "support":
        return renderSupport();
      case "adManager":
        return <p style={{ padding: "40px" }}>Coming Soon..</p>;
      default:
        return null;
    }
  };

  return (
    <>
      <div id="profile_menu" className="modal" style={{ display: "block" }}>
        <div className="modal-content-400">
          <span className="close" onClick={onClose}>
            &times;
          </span>
          <div
            id="UM-section"
            className="modal-body"
            style={{ padding: "10px", backgroundColor: "lightgreen" }}
          >
            {renderContent()}
            {(statusMessage || errorMessage) && (
              <div
                style={{
                  marginTop: "10px",
                  textAlign: "center",
                  fontWeight: "bold",
                  color: errorMessage ? "red" : "black",
                }}
              >
                {statusMessage}
                {loadingDots}
                {errorMessage && <div>{errorMessage}</div>}
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmationDialog
        show={showConfirmDialog}
        action={currentAction}
        onConfirm={handleConfirmAction}
        onCancel={handleCancelAction}
      />
    </>
  );
};

export default ProfileMenu;
